import React from 'react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { 
  ArrowLeft, 
  ArrowUp, 
  Trash2, 
  Edit2, 
  FileText, 
  Image as ImageIcon,
  X,
  ChevronRight
} from 'lucide-react';
import billService from '../../services/billService';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount || 0);
};

const formatDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  const options = { hour: '2-digit', minute: '2-digit', hour12: true, day: '2-digit', month: 'short', year: 'numeric' };
  // Approximate hh:mm a dd MMM yyyy
  const parts = d.toLocaleString('en-IN', options).split(', ');
  return parts.reverse().join(' '); 
};

const ExpenseDetailsView = ({ bill, onBack, onEdit, onDeleted }) => {
  const { t } = useLang();
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [previewImageUrl, setPreviewImageUrl] = React.useState(null);

  if (!bill) return null;

  const categoryName = bill.expenseCategory ? t(bill.expenseCategory) : (bill.items?.[0]?.item?.name || 'Unknown');
  const hasPhotos = (bill.photoUrls?.length > 0) || (bill.privatePhotos?.length > 0);

  const handleDelete = async () => {
    const success = await billService.deleteBill(bill.id);
    if (success) {
      setShowDeleteConfirm(false);
      if (onDeleted) onDeleted();
    } else {
      alert("Failed to delete expense");
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={onBack} style={styles.backBtn}><ArrowLeft size={20} /></button>
          <h3 style={styles.title}>{t(LangKeys.expense_number_prefix || 'Expense #')}{bill.billNumber}</h3>
        </div>
      </div>

      <div style={styles.scrollContent}>
        {/* MAIN CARD */}
        <div style={styles.mainCard}>
           <div style={styles.topRow}>
              <div style={styles.iconCircle}>
                 <ArrowUp size={24} color="#B91C1C" />
              </div>
              <div style={styles.infoCol}>
                 <span style={styles.catName}>{categoryName}</span>
                 <span style={styles.dateText}>{formatDate(bill.date)}</span>
              </div>
              <span style={styles.amountText}>{formatCurrency(bill.finalBillAmount)}</span>
           </div>
           
           <div style={styles.divider} />

           <button style={styles.editCardBtn} onClick={() => onEdit(bill)}>
              {t(LangKeys.edit_expense)}
           </button>
        </div>

        {/* NOTES */}
        {bill.privateNotes && (
          <div style={styles.sectionCard}>
             <span style={styles.sectionLabel}>{t(LangKeys.note)}</span>
             <p style={styles.noteBody}>{bill.privateNotes}</p>
          </div>
        )}

        {/* PHOTOS */}
        {hasPhotos && (
          <div style={styles.sectionCard}>
             <span style={styles.sectionLabel}>{t(LangKeys.photo)}</span>
             <div style={styles.photoGrid}>
                 {bill.photoUrls?.map((url, i) => (
                   <div key={i} style={{ ...styles.photoThumb, cursor: 'pointer' }} onClick={() => setPreviewImageUrl(url)}>
                      <img src={url} alt="Expense" style={styles.img} />
                   </div>
                 ))}
                 {bill.privatePhotos?.map((p, i) => {
                   const localUrl = URL.createObjectURL(p);
                   return (
                     <div key={`p-${i}`} style={{ ...styles.photoThumb, cursor: 'pointer' }} onClick={() => setPreviewImageUrl(localUrl)}>
                        <img src={localUrl} alt="Private" style={styles.img} />
                     </div>
                   );
                 })}
             </div>
          </div>
        )}
      </div>

      <div style={styles.bottomBar}>
         <button style={styles.deleteBtn} onClick={() => setShowDeleteConfirm(true)}>
            <Trash2 size={18} />
            <span>{t(LangKeys.delete_expense || 'DELETE EXPENSE').toUpperCase()}</span>
         </button>
      </div>

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteConfirm && (
        <div style={styles.modalOverlay}>
           <div style={styles.confirmBox}>
              <h4 style={styles.confirmTitle}>{t(LangKeys.delete)}</h4>
              <p style={styles.confirmText}>{t(LangKeys.delete_expense_confirmation)}</p>
              <div style={styles.confirmActions}>
                 <button style={styles.cancelBtn} onClick={() => setShowDeleteConfirm(false)}>{t(LangKeys.cancel).toUpperCase()}</button>
                 <button style={styles.deleteConfirmBtn} onClick={handleDelete}>{t(LangKeys.delete).toUpperCase()}</button>
              </div>
           </div>
        </div>
      )}

      {/* IMAGE PREVIEW OVERLAY */}
      {previewImageUrl && (
        <div 
          style={styles.previewOverlay} 
          onClick={() => setPreviewImageUrl(null)}
        >
          <button 
            style={styles.closePreviewBtn} 
            onClick={(e) => { e.stopPropagation(); setPreviewImageUrl(null); }}
          >
            <X size={24} color="white" />
          </button>
          <div style={styles.previewImgContainer} onClick={(e) => e.stopPropagation()}>
            <img 
              src={previewImageUrl} 
              alt="Expense Full Preview" 
              style={styles.previewImg} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { display: 'flex', flexDirection: 'column', height: '100%', background: '#F3F4F6' },
  header: { padding: '20px 24px', background: 'white', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  backBtn: { width: '36px', height: '36px', borderRadius: '10px', border: 'none', background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  title: { fontSize: '18px', fontWeight: '800', margin: 0 },
  scrollContent: { flex: 1, padding: '16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px' },
  mainCard: { background: 'white', borderRadius: '16px', border: '1px solid #E5E7EB', padding: '16px', display: 'flex', flexDirection: 'column' },
  topRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  iconCircle: { width: '48px', height: '48px', borderRadius: '24px', background: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  infoCol: { flex: 1, display: 'flex', flexDirection: 'column' },
  catName: { fontSize: '16px', fontWeight: '800', color: '#1E293B' },
  dateText: { fontSize: '13px', color: '#64748B', fontWeight: '600' },
  amountText: { fontSize: '18px', fontWeight: '900', color: '#B91C1C' },
  divider: { height: '1px', background: '#F1F5F9', margin: '16px 0' },
  editCardBtn: { border: 'none', background: 'transparent', color: '#1D4ED8', fontWeight: '800', fontSize: '14px', letterSpacing: '0.5px', padding: '8px', cursor: 'pointer' },
  sectionCard: { background: 'white', borderRadius: '16px', border: '1px solid #E5E7EB', padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' },
  sectionLabel: { fontSize: '12px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase' },
  noteBody: { fontSize: '15px', color: '#1E293B', margin: 0, lineHeight: '1.5' },
  photoGrid: { display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '4px' },
  photoThumb: { width: '80px', height: '80px', borderRadius: '12px', background: '#F1F5F9', overflow: 'hidden', border: '1px solid #E2E8F0' },
  img: { width: '100%', height: '100%', objectFit: 'cover' },
  bottomBar: { padding: '16px 20px', background: 'white', borderTop: '1px solid #E5E7EB' },
  deleteBtn: { width: '100%', height: '50px', borderRadius: '12px', border: '1px solid #EF4444', background: 'white', color: '#EF4444', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' },
  modalOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  confirmBox: { background: 'white', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '340px', display: 'flex', flexDirection: 'column', gap: '16px' },
  confirmTitle: { fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0 },
  confirmText: { fontSize: '14px', color: '#64748B', lineHeight: '1.5', margin: 0 },
  confirmActions: { display: 'flex', gap: '12px' },
  cancelBtn: { flex: 1, height: '40px', borderRadius: '10px', border: '1px solid #E2E8F0', background: 'white', color: '#64748B', fontWeight: '700', fontSize: '13px', cursor: 'pointer' },
  deleteConfirmBtn: { flex: 1, height: '40px', borderRadius: '10px', border: 'none', background: '#EF4444', color: 'white', fontWeight: '700', fontSize: '13px', cursor: 'pointer' },
  previewOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px'
  },
  closePreviewBtn: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    background: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    width: '44px',
    height: '44px',
    borderRadius: '22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  previewImgContainer: {
    maxWidth: '90%',
    maxHeight: '90%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
  },
  previewImg: {
    maxWidth: '100%',
    maxHeight: '85vh',
    objectFit: 'contain',
    borderRadius: '12px'
  }
};

export default ExpenseDetailsView;
